// Selector searchable de cuentas-banco externas del proveedor configurado.
// Carga lazy: solo dispara la peticion cuando el componente esta montado
// (controlado desde el padre via `show`). Sigue el patron de
// SelectorCuentaExterna pero sin paginacion virtual ya que las cuentas-banco
// son tipicamente pocas (< 50).

import React, { useEffect, useMemo } from 'react';
import Select, { components as RSComponents } from 'react-select';
import { Alert, Spinner, Button } from 'react-bootstrap';
import { FaUniversity } from 'react-icons/fa';
import { useCuentasBancoExternas } from '../../hooks/useCuentasBancoExternas';

const selectStyles = {
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({ ...base, minHeight: 38 }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    padding: '6px 10px',
    backgroundColor: state.isFocused ? '#e7f1ff' : state.isSelected ? '#cfe2ff' : 'white',
    color: '#212529',
    cursor: 'pointer'
  })
};

function OptionRender({ data, ...props }) {
  return (
    <RSComponents.Option {...props} data={data}>
      <div className="d-flex justify-content-between align-items-center gap-2">
        <div className="d-flex align-items-center gap-2 flex-grow-1 text-truncate">
          <code style={{ fontSize: 12, color: '#0d6efd' }}>{data.value}</code>
          <span className="text-truncate">{data.label}</span>
        </div>
        {data.raw.type && (
          <span className="badge bg-info text-dark" style={{ fontSize: 10 }}>
            {data.raw.type}
          </span>
        )}
      </div>
    </RSComponents.Option>
  );
}

function SingleValueRender({ data, ...props }) {
  return (
    <RSComponents.SingleValue {...props}>
      <code style={{ fontSize: 12, color: '#0d6efd' }} className="me-2">{data.value}</code>
      <span>{data.label}</span>
    </RSComponents.SingleValue>
  );
}

const SelectorCuentaBanco = ({ value, onSelect }) => {
  const { data, isLoading, isError, error, refetch } = useCuentasBancoExternas({
    enabled: true
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const cuentas = data?.cuentas || [];
  const configurado = data?.configurado;

  const opciones = useMemo(
    () => cuentas.map(c => ({ value: c.id, label: c.name, raw: c })),
    [cuentas]
  );

  const selected = opciones.find(o => o.value === value) || null;

  const handleChange = (opt) => {
    if (onSelect) onSelect(opt ? opt.raw : null);
  };

  if (isLoading) {
    return (
      <div className="d-flex align-items-center gap-2 text-muted small py-2">
        <Spinner animation="border" size="sm" />
        Consultando cuentas-banco del proveedor…
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="warning" className="py-2 small mb-0">
        No se pudieron consultar las cuentas-banco. {error?.message || ''}
        <Button size="sm" variant="link" onClick={() => refetch()} className="p-0 ms-2">
          Reintentar
        </Button>
      </Alert>
    );
  }

  if (!configurado || cuentas.length === 0) {
    return (
      <Alert variant="light" className="py-2 small mb-0 border">
        No hay cuentas-banco disponibles en el proveedor o el endpoint
        (document_type=transferencia_bancaria) no esta configurado.
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center mb-2 flex-wrap gap-2">
        <FaUniversity className="text-primary" />
        <strong>Relacionar con cuenta-banco del proveedor</strong>
        <span className="badge bg-secondary">{cuentas.length}</span>
      </div>
      <Select
        options={opciones}
        value={selected}
        onChange={handleChange}
        placeholder="Buscar cuenta-banco por id o nombre…"
        isClearable
        isSearchable
        noOptionsMessage={() => 'Sin coincidencias'}
        styles={selectStyles}
        components={{ Option: OptionRender, SingleValue: SingleValueRender }}
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
      <small className="text-muted d-block mt-1">
        El id seleccionado se guarda como <code>bankId</code> en la cuenta contable.
      </small>
    </div>
  );
};

export default SelectorCuentaBanco;
